import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { Option } from '../../core/components/Fields/AutoCompleteField';
import {
    buildMatchingDropdownOptions,
    fetchLookupOptions,
    MASTERDATA_BROWSER_KEY,
    MASTERDATA_DEFAULT_BROWSER_KEY,
} from './useLookup';

const ORDER_LINE_ITEMS_LIMIT = 6;

// Browse the order's lines via the primary article-id field: an empty search on it returns every
// line of the referenced order (the metadata service filters by order_number + partition only when
// the search term is empty). Other target fields -- e.g. article_description -- return nothing on an
// empty term. Selection uses each line's `id`, so the browse field only affects the displayed value.
const ORDER_LINE_BROWSE_FIELD = 'article_id1';

/**
 * Hook for matching-enabled article fields (OC / invoice line items). It fetches the referenced
 * order's line items so the caller can offer them whenever the order-scoped live search returns
 * nothing (the extracted or typed article is not on the order) instead of a "no results"
 * message.
 *
 * The list already exists on the backend: an empty search term plus the `order_number` kwarg makes
 * the metadata service return the referenced order's line items.
 */
export const useOrderLineItemsFallback = ({
    hasMatching,
    matchingLookupType,
    matchingDocumentNumber,
    lookupDefinitionId,
    partitionId,
    extraSearchTerms,
}: {
    hasMatching: boolean;
    matchingLookupType: string;
    matchingDocumentNumber?: string;
    lookupDefinitionId?: string;
    partitionId?: string;
    extraSearchTerms?: Record<string, any>;
}) => {
    const { t } = useTranslation('masterdata');
    const client = useApolloClient();

    const isEnabled = hasMatching && !!matchingDocumentNumber && !!lookupDefinitionId;

    const [orderLines, setOrderLines] = useState<Option[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadOrderLines = useCallback(() => {
        if (!isEnabled) return;
        setIsLoading(true);
        // Empty search term + the `order_number` kwarg returns the referenced order's line items;
        // the metadata service filters by `order_number` + partition regardless of the target field.
        fetchLookupOptions({
            client,
            lookupDefinitionId,
            lookupFieldName: ORDER_LINE_BROWSE_FIELD,
            partitionId,
            extraSearchTerms,
        })('', 0, ORDER_LINE_ITEMS_LIMIT)
            .then((options: Option[]) => setOrderLines(options))
            .finally(() => setIsLoading(false));
    }, [isEnabled, client, lookupDefinitionId, partitionId, extraSearchTerms]);

    const orderLineOptions: Option[] = useMemo(() => {
        if (!isEnabled || !orderLines.length) return [];

        return buildMatchingDropdownOptions({
            headerLabel: t(
                matchingLookupType === 'OfferMatchingLookupType'
                    ? 'assistance.orderLineItems.headerOffer'
                    : 'assistance.orderLineItems.header',
                { orderNumber: matchingDocumentNumber }
            ),
            items: orderLines,
            browserButtons: [
                {
                    label: t(
                        matchingLookupType === 'OfferMatchingLookupType'
                            ? 'browser.openBrowserOfferItems'
                            : 'browser.openBrowserOrderItems'
                    ),
                    value: MASTERDATA_BROWSER_KEY,
                },
                { label: t('browser.searchEntireData'), value: MASTERDATA_DEFAULT_BROWSER_KEY },
            ],
        });
    }, [isEnabled, orderLines, matchingDocumentNumber, matchingLookupType, t]);

    return {
        isEnabled,
        orderLineOptions,
        isLoading: isEnabled && isLoading,
        loadOrderLines,
    };
};
